import { format, isValid, parse } from 'date-fns';
import {
  TIME_INPUT_MAIN_SEPARATOR,
  TIME_INPUT_SEPARATORS,
  TimeInputFormat,
} from './constants';

export type ParsedDate = {
  format: TimeInputFormat;
  value: Date;
};

export const TIME_PICKER_FORMATS: TimeInputFormat[] = ([
  // formats order important
  { pattern: `h${TIME_INPUT_MAIN_SEPARATOR}mm a`, exact: true },
  { pattern: `h${TIME_INPUT_MAIN_SEPARATOR}mma`, exact: true },
  { pattern: `h${TIME_INPUT_MAIN_SEPARATOR}mm ` },
  { pattern: 'ha' },
  { pattern: 'h a' },
  {
    pattern: `H${TIME_INPUT_MAIN_SEPARATOR}mm`,
    is24: true,
    exact: true,
    isValid: (value) => {
      if (!value) {
        return false;
      }
      const separator = TIME_INPUT_SEPARATORS.find(
        (timeInputSeparator) => value.indexOf(timeInputSeparator) !== -1
      );
      return separator && (value.split(separator)[1] || '').length > 1;
    },
  },
  {
    pattern: `H${TIME_INPUT_MAIN_SEPARATOR}mm`,
    is24: true,
    invalid: true,
  },
  {
    pattern: 'H',
    is24: true,
    transform: (value) =>
      (/^((1[3-9])|(2[0-4]))\s?pm?$/.exec(value) || [value, value])[1],
  },
  { pattern: `H${TIME_INPUT_MAIN_SEPARATOR}`, is24: true, invalid: true },
] as TimeInputFormat[])
  .reduce(
    (formatsAr: [], rawFormat: TimeInputFormat) => [
      ...formatsAr,
      ...TIME_INPUT_SEPARATORS.map((separator) => ({
        ...rawFormat,
        pattern: rawFormat.pattern.replace(
          TIME_INPUT_MAIN_SEPARATOR,
          separator
        ),
      })),
    ],
    []
  )
  .sort((formatA, formatB) => {
    if (formatA.invalid !== formatB.invalid) {
      return formatA.invalid ? 1 : -1;
    }
    if (formatA.is24 !== formatB.is24) {
      return formatA.is24 ? 1 : -1;
    }
    return 0;
  });

export const dateToTimeString = (date?: Date): string =>
  date ? format(date, TIME_PICKER_FORMATS[0].pattern) : '';

export const parseTimeString: (timeString: string) => ParsedDate | null = (
  timeString
) => {
  let parsedValue = null;

  const parsedFormat = TIME_PICKER_FORMATS.find((testingFormat) => {
    const transformed = testingFormat.transform
      ? testingFormat.transform(timeString)
      : timeString;

    const time = parse(transformed, testingFormat.pattern, new Date());

    if (
      isValid(time) &&
      (!testingFormat.isValid || testingFormat.isValid(transformed))
    ) {
      parsedValue = time;
      return true;
    }
    return false;
  });

  return parsedValue && parsedFormat
    ? {
        value: parsedValue,
        format: parsedFormat,
      }
    : null;
};

export const isTimeEqual = (date1?: Date | null, date2?: Date | null) => {
  if (!date1 && !date2) {
    return true;
  }
  if (!date1 || !date2) {
    return false;
  }
  return (
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes()
  );
};
